import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';

import { apiGet } from '@/lib/http/client';

import { applyServerSnapshot, replaceAll } from '../store/agentFeedStore';
import { useAgentFeedItems } from '../store/hooks';
import type {
  AgentFeedItem,
  DoneTodayData,
  DoneTodayExecutedItem,
  DoneTodayItem,
  DoneTodayProposalItem,
} from '../types';

// ---------------------------------------------------------------------------
// Wire types (snake_case — matches backend JSON shape)
// ---------------------------------------------------------------------------

interface AgentActivityWireInProgressItem {
  id: string;
  type: 'trigger' | 'conversation';
  status: 'evaluating' | 'preparing';
  entity_id?: string | null;
  entity_name: string;
  entity_type_name?: string | null;
  trigger_sources: string[];
  fire_at?: string | null;
  conversation_id?: number | null;
  trigger_id?: number | null;
  created_at: string;
}

interface AgentActivityWireDoneTodayItem {
  id: string;
  trigger_entity_id?: string | null;
  trigger_entity_name?: string | null;
  trigger_entity_type_name?: string | null;
  subject_entity_id?: string | null;
  subject_entity_name?: string | null;
  trigger_sources: string[];
  completed_at: string;
}

interface AgentActivityWireNoActionItem extends AgentActivityWireDoneTodayItem {
  reason?: string | null;
}

interface AgentActivityWireDoneTodayProposalItem extends AgentActivityWireDoneTodayItem {
  proposal_id: number;
  title: string;
}

interface AgentActivityWireDoneTodayExecutedItem {
  id: number;
  title: string;
  executed_at: string;
}

interface AgentActivityResponse {
  in_progress: AgentActivityWireInProgressItem[];
  done_today: {
    no_action: AgentActivityWireNoActionItem[];
    proposals_created: AgentActivityWireDoneTodayProposalItem[];
    proposals_executed: AgentActivityWireDoneTodayExecutedItem[];
  };
}

// ---------------------------------------------------------------------------
// Query key factory
// ---------------------------------------------------------------------------

export const agentFeedQueryKey = (orgId: number) =>
  ['agentFeed', orgId] as const;

// ---------------------------------------------------------------------------
// Pure mapping helpers
// ---------------------------------------------------------------------------

function mapInProgress(item: AgentActivityWireInProgressItem): AgentFeedItem {
  return {
    id: item.id,
    type: item.type,
    status: item.status,
    entityId: item.entity_id ?? undefined,
    entityName: item.entity_name,
    entityTypeName: item.entity_type_name ?? undefined,
    triggerSources: item.trigger_sources,
    fireAt: item.fire_at ?? undefined,
    conversationId: item.conversation_id ?? undefined,
    triggerId: item.trigger_id ?? undefined,
    createdAt: item.created_at,
  };
}

function mapDoneToday(
  wire: AgentActivityResponse['done_today']
): DoneTodayData {
  return {
    noAction: wire.no_action.map<DoneTodayItem>(i => ({
      id: i.id,
      triggerEntityId: i.trigger_entity_id ?? undefined,
      triggerEntityName: i.trigger_entity_name ?? undefined,
      triggerEntityTypeName: i.trigger_entity_type_name ?? undefined,
      subjectEntityId: i.subject_entity_id ?? undefined,
      subjectEntityName: i.subject_entity_name ?? undefined,
      triggerSources: i.trigger_sources,
      completedAt: i.completed_at,
      reason: i.reason ?? undefined,
    })),
    proposalsCreated: wire.proposals_created.map<DoneTodayProposalItem>(i => ({
      id: i.id,
      triggerEntityId: i.trigger_entity_id ?? undefined,
      triggerEntityName: i.trigger_entity_name ?? undefined,
      triggerEntityTypeName: i.trigger_entity_type_name ?? undefined,
      subjectEntityId: i.subject_entity_id ?? undefined,
      subjectEntityName: i.subject_entity_name ?? undefined,
      triggerSources: i.trigger_sources,
      completedAt: i.completed_at,
      proposalId: i.proposal_id,
      title: i.title,
    })),
    proposalsExecuted: wire.proposals_executed.map<DoneTodayExecutedItem>(
      i => ({
        id: i.id,
        title: i.title,
        executedAt: i.executed_at,
      })
    ),
  };
}

// ---------------------------------------------------------------------------
// Result type
// ---------------------------------------------------------------------------

export interface UseAgentFeedResult {
  inProgress: ReadonlyArray<AgentFeedItem>;
  doneToday: DoneTodayData | null;
  isLoading: boolean;
  error: Error | null;
  counts: { evaluating: number; preparing: number; doneToday: number };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useAgentFeed(orgId: number | null): UseAgentFeedResult {
  const enabled = orgId !== null && orgId > 0;

  // Wall-clock time the in-flight REST request began. Captured in queryFn so
  // applyServerSnapshot can tell which WS events arrived during the fetch (and
  // therefore must survive the snapshot replacement). React Query dedupes
  // concurrent fetches per key, so this reflects the fetch that produced
  // query.data when the effect below reads it.
  const fetchStartedAtRef = useRef(0);

  const query = useQuery({
    queryKey: agentFeedQueryKey(orgId ?? 0),
    queryFn: () => {
      fetchStartedAtRef.current = Date.now();
      return apiGet<AgentActivityResponse>(
        `/api/orgs/${orgId}/reasoning/agent-activity/`
      );
    },
    enabled,
    staleTime: 30_000,
  });

  useEffect(() => {
    // When the query is disabled (no org) or has no data yet (org switch in
    // flight), hard-reset the store so stale items from the previous org never
    // leak into the new view.
    if (!enabled || !query.data) {
      replaceAll([]);
      return;
    }
    // Reconcile the REST snapshot into the store. Unlike a blind replaceAll,
    // this preserves WS events that arrived while the fetch was in flight,
    // avoiding transiently-missing in-progress items.
    //
    // When fetchStartedAtRef is still 0 (queryFn hasn't fired — React Query
    // served cached data), fall back to replaceAll. With fetchStartedAt=0
    // every WS timestamp would be "newer than the snapshot", leaking stale
    // items from a previous org or mount.
    const mapped = query.data.in_progress.map(mapInProgress);
    if (fetchStartedAtRef.current === 0) {
      replaceAll(mapped);
    } else {
      applyServerSnapshot(mapped, fetchStartedAtRef.current);
    }
  }, [enabled, query.data]);

  const inProgress = useAgentFeedItems();

  const doneToday = useMemo<DoneTodayData | null>(
    () => (query.data ? mapDoneToday(query.data.done_today) : null),
    [query.data]
  );

  // inProgress reference changes on every store mutation (new array from useSyncExternalStore),
  // so counts recomputes on each WS event. This is intentional: both applyServerSnapshot()
  // and replaceAll() always emit so REST snapshot refreshes propagate even when content
  // appears identical. The array is expected to be small (<20 items), so the filters are trivial.
  const counts = useMemo(() => {
    const evaluating = inProgress.filter(i => i.status === 'evaluating').length;
    const preparing = inProgress.filter(i => i.status === 'preparing').length;
    const doneTodayCount = doneToday
      ? doneToday.noAction.length +
        doneToday.proposalsCreated.length +
        doneToday.proposalsExecuted.length
      : 0;
    return { evaluating, preparing, doneToday: doneTodayCount };
  }, [inProgress, doneToday]);

  return {
    inProgress,
    doneToday,
    isLoading: query.isLoading,
    error: query.error ?? null,
    counts,
  };
}
